import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import {
    PAGE_BG, CARD_BG, BORDER, TEXT_MAIN, TEXT_MUTED, PRIMARY, PRIMARY_HOVER, CARD_RADIUS, FONT,
    SUCCESS_COLOR, SUCCESS_BG, ERROR_COLOR, ERROR_BG, LEAVE_COLOR, LEAVE_BG,
    FS_TITLE, FS_BODY, FS_LABEL,
} from './tokens';

const ICON_PATHS = {
    success: { bg: SUCCESS_BG, stroke: SUCCESS_COLOR, strokeWidth: 2.5, d: 'M5 13l4 4L19 7' },
    error: { bg: ERROR_BG, stroke: ERROR_COLOR, strokeWidth: 2, d: 'M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' },
    leave: { bg: LEAVE_BG, stroke: LEAVE_COLOR, strokeWidth: 2, d: 'M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm0 5v5l3 3' },
};

function StatusIcon({ variant }) {
    const { bg, stroke, strokeWidth, d } = ICON_PATHS[variant] || ICON_PATHS.error;
    return (
        <Box
            sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
            }}
        >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d={d} stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </Box>
    );
}

export default function QuizResult({ variant, isSuccess, message, soleMessage, onReturn, t }) {
    const resolvedVariant = variant ?? (isSuccess ? 'success' : 'error');

    const icon = <StatusIcon variant={resolvedVariant} />;

    const title = resolvedVariant === 'success'
        ? t('quiz_success_title')
        : resolvedVariant === 'leave'
            ? t('quiz_leave_title')
            : t('quiz_error_title');

    const body = resolvedVariant === 'success'
        ? t('quiz_success_body')
        : resolvedVariant === 'leave'
            ? t('postpone_survey')
            : soleMessage
                ? message
                : t('quiz_error_body');

    const buttonLabel = resolvedVariant === 'leave' ? t('quiz_restart') : t('quiz_try_again');

    const buttonSx = {
        backgroundColor: PRIMARY,
        px: 4,
        py: 1.25,
        borderRadius: '8px',
        fontWeight: 600,
        fontSize: FS_BODY,
        textTransform: 'none',
        '&:hover': { backgroundColor: PRIMARY_HOVER },
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: PAGE_BG, fontFamily: FONT }}>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: 480,
                        backgroundColor: CARD_BG,
                        borderRadius: CARD_RADIUS,
                        border: `1px solid ${BORDER}`,
                        p: { xs: 3, sm: 4.5 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                    }}
                >
                    {icon}

                    {title && (
                        <Typography
                            component="h1"
                            sx={{
                                fontWeight: 700,
                                fontSize: FS_TITLE,
                                color: TEXT_MAIN,
                                mb: 1.5,
                                lineHeight: 1.25,
                            }}
                        >
                            {title}
                        </Typography>
                    )}

                    <Typography sx={{ fontSize: FS_BODY, color: TEXT_MUTED, lineHeight: 1.65, mb: 3 }}>
                        {body}
                    </Typography>

                    {message && resolvedVariant === 'success' && (
                        <Typography sx={{ fontSize: FS_LABEL, color: TEXT_MUTED, mb: 3 }}>
                            {t('quiz_reply_id')}: <strong>{message}</strong>
                        </Typography>
                    )}
                    {message && resolvedVariant === 'error' && !soleMessage && (
                        <Typography sx={{ fontSize: FS_LABEL, color: TEXT_MUTED, mb: 3 }}>
                            {message}
                        </Typography>
                    )}

                    {resolvedVariant !== 'success' && (
                        <Button
                            variant="contained"
                            onClick={onReturn}
                            sx={buttonSx}
                        >
                            {buttonLabel}
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
